/*
 * This component handles the functionality for GWP (Gift With Purchase) products.
 * It retrieves GWP products from the storefront API using GraphQL queries
 * and displays them in a modal.
 */

import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import store from 'store';
import * as queries from 'lib/graphql/queries';
import storefront from 'lib/storefront';
import { formatPrice, convertGQLToProductJson } from 'utils/general';
import { i18n } from 'lib/i18n.js';

export default {
  components: {
    modal: () => import('../modal/modal.vue'),
    'vue-image': () => import('../vue-image/vue-image.vue'),
    'gwp-item': () => import('../gwp/gwp-item.vue'),
    'product-card': () => import('../product-card/product-card.vue'),
  },
  i18n,
  store,
  name: 'gwp',
  data() {
    return {
      filteredRewardProducts: [],
    };
  },
  created() {
    this.getFilteredRewardProducts();
  },
  mounted() {
    if (!this.rewardProducts.length) {
      this.requestRewardsCollection(this.gwpHandle);
    }
  },
  watch: {
    userSelection() {
      if (this.userSelection === this.limitSelection) {
        setTimeout(() => {
          this.closeModal();
        }, 1000);
      }
    },
  },
  computed: {
    ...mapState('global', ['cart']),
    ...mapGetters('global', [
      'rewardProducts',
      'rewardProductsAvailable',
      'rewardProductsLimit',
      'rewardUserList',
      'rewardUserListNotAdded',
      'userSelectionReady',
      'isGWPLoading',
      'gwpType',
      'gwpThresholds',
      'totalCartAmount',
      'gwpHandle',
    ]),
    limitSelection() {
      return this.rewardProductsLimit;
    },
    userSelection() {
      return this.rewardUserList.length;
    },
    loadingText() {
      return this.$t('cart.gwp.loading');
    },
  },
  methods: {
    ...mapActions('global', [
      'openModal',
      'closeModal',
      'setGWPLoading',
      'requestRewardsCollection',
      'clearUserSelection',
      'removeAllGWP',
      'clearGWP',
    ]),
    ...mapActions('cart', [
      'addToCart',
      'updateItemQuantity',
      'openCart',
      'closeCart',
    ]),
    ...mapMutations('global', ['setGWPHandle']),

    CTAAvailable() {
      return this.rewardUserListNotAdded > 0;
    },
    formattedTitle(string) {
      return string.split(window.__GLOBAL__.productDelimiter)[0];
    },
    variantTitle(string) {
      return string.split(window.__GLOBAL__.productDelimiter)[1];
    },
    formattedPrice(price) {
      return formatPrice(price);
    },
    async getFilteredRewardProducts() {
      const cookies = document.cookie.split('; ').reduce((prev, current) => {
        const [name, ...value] = current.split('=');
        prev[name] = value.join('=');
        return prev;
      }, {});
      const exclusivePromoCode = cookies.UrlPromoCode;
      const exclusiveProducts = this.getExclusiveProducts(this.rewardProducts);
      const restrictedReplacedProducts =
        await this.getReplacedRestrictedProducts(exclusiveProducts);

      const filteredProducts = restrictedReplacedProducts
        .filter((product) => {
          const restrictedQtyTag = product.tags?.find((tag) =>
            tag.includes(`shipping-restricted-qty|${window.shippingCode}`),
          );
          if (restrictedQtyTag) {
            const restrictedQty = restrictedQtyTag.split(
              `shipping-restricted-qty|${window.shippingCode}|`,
            )[1];
            const qtyInCart = this.cart.items.reduce((acc, item) => {
              if (+item.product_id === +product.id && !item.properties._gwp) {
                acc += item.quantity;
              }
              return acc;
            }, 0);
            if (qtyInCart >= Number(restrictedQty)) {
              this.rewardUserList = this.rewardUserList.filter(
                (item) => +item.id !== +product.id,
              );
              return false;
            }
          }
          return true;
        })
        .reduce((unique, product) => {
          const hasReplacementTag = product.tags?.find((tag) =>
            tag.includes(`shipping-restricted-replacement|`),
          );
          if (hasReplacementTag) {
            unique.push({ ...product });
          } else {
            const title = product?.title.split(
              window.__GLOBAL__.productDelimiter,
            )[0];
            if (!unique.some((obj) => obj.title.includes(title))) {
              const eligibleVariants = product.variants.filter(
                (variant) => variant.isGWPVariant,
              );
              if (eligibleVariants.length) {
                unique.push({ ...product, variants: eligibleVariants });
              }
            }
          }
          return unique;
        }, []);

      if (!filteredProducts.length) {
        console.warn('There are no products eligible to be GWP');
      }

      this.filteredRewardProducts = filteredProducts;
    },
    getExclusiveProducts(products) {
      const filtereeProducts = (products ?? []).filter((product) => {
        const exclusivePromoCodes = product.tags?.filter((tag) =>
          tag.match(/^GWP Exclusive\|/i),
        );
        if (exclusivePromoCodes.length) {
          return exclusivePromoCodes.some((tag) =>
            tag.includes(exclusivePromoCode),
          );
        }
        return true;
      });
      return filtereeProducts;
    },
    async getReplacedRestrictedProducts(products) {
      const replacedProducts = await Promise.all(
        products?.map(async (product) => {
          if (
            product.tags?.some((tag) =>
              tag.includes('shipping-restricted-replacement|'),
            )
          ) {
            const restrictedProductTag = product.tags.find((tag) =>
              tag.includes(
                `shipping-restricted-replacement|${window.shippingCode}`,
              ),
            );
            if (restrictedProductTag) {
              const restrictedProductId = restrictedProductTag.split(
                `shipping-restricted-replacement|${window.shippingCode}|`,
              )[1];
              const restrictedProduct = await this.getRestrictedProductById(
                restrictedProductId,
              );
              if (!restrictedProduct) {
                console.warn(`No replacement for ${product.title} was found`);
                return false;
              }
              return restrictedProduct;
            }
          }
          return product;
        }),
      );
      return replacedProducts.filter(Boolean);
    },
    async getRestrictedProductById(id) {
      const response = await storefront.request(queries.productTagByVariantId, {
        id: 'gid://shopify/ProductVariant/' + id,
      });
      return response.node
        ? convertGQLToProductJson(response.node.product)
        : false;
    },
  },
};
